import polygonClipping from 'polygon-clipping'

/**
 * Combines GRB parcels into the projectzone. Works on plain MultiPolygon coordinates
 * (polygons -> rings -> [x, y]) in a metric projection, so it does not depend on OpenLayers.
 * Parcels that share an edge merge into one polygon; parcels that do not stay separate polygons.
 */

// Adjacent GRB parcels share their corners exactly, but the zone has made a reprojection round trip
// (map -> stored EPSG:4326 -> OVAM EPSG:3812 -> map) since it was built. A parcel corner this close
// to a zone corner is moved onto it first, or the float noise leaves a hairline gap or sliver along
// the shared edge and the parcels would not merge.
const SNAP_TOLERANCE = 0.01
// Pieces and holes smaller than this (square map units) are leftovers of that same noise.
const MIN_AREA = 0.01

function ringArea(ring) {
  let sum = 0
  for (let i = 0; i < ring.length - 1; i++) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1]
  }
  return Math.abs(sum) / 2
}

function snapToCorners(polygons, corners) {
  return polygons.map(polygon => polygon.map(ring => ring.map(([x, y]) => {
    const corner = corners.find(c => Math.abs(c[0] - x) <= SNAP_TOLERANCE && Math.abs(c[1] - y) <= SNAP_TOLERANCE)
    return corner ? [corner[0], corner[1]] : [x, y]
  })))
}

function withoutSlivers(polygons) {
  return polygons
    .filter(polygon => ringArea(polygon[0]) >= MIN_AREA)
    .map(([outer, ...holes]) => [outer, ...holes.filter(hole => ringArea(hole) >= MIN_AREA)])
}

function combine(operation, zone, parcel) {
  const zonePolygons = zone || []
  const parcelPolygons = snapToCorners(parcel || [], zonePolygons.flat(2))
  if (!parcelPolygons.length) {
    return zonePolygons
  }
  return withoutSlivers(polygonClipping[operation](zonePolygons, parcelPolygons))
}

/**
 * The zone with the parcel added.
 * @param {number[][][][]} zone MultiPolygon coordinates of the current zone ([] when there is none)
 * @param {number[][][][]} parcel MultiPolygon coordinates of the parcel
 * @returns {number[][][][]}
 */
export function addParcel(zone, parcel) {
  return combine('union', zone, parcel)
}

/**
 * The zone with the parcel cut out; [] when nothing is left.
 * @param {number[][][][]} zone
 * @param {number[][][][]} parcel
 * @returns {number[][][][]}
 */
export function removeParcel(zone, parcel) {
  return combine('difference', zone, parcel)
}
